<template>
  <div class="auth-page">
    <div class="auth-page__content">
      <img src="@/assets/images/header-logo.png" alt="Lamar Health" class="auth-page__logo" />
      <h1 class="auth-page__title">Reset Password</h1>

      <div class="auth-page__card">
        <div v-if="!passwordRequestEmailSent">
          <v-form @submit.prevent="passwordSending">
            <p class="text-center mb-4 text-primary weight-500">
              Forgot your password? No problem! Enter your registered email address below, and we'll send you a link to
              reset your password.
            </p>

            <v-text-field
              v-model="userEmail"
              label="Email Address"
              type="email"
              placeholder="example@findsunrise.com"
              :error-messages="!isEmailValid && formButtonClicked ? 'Please enter a valid email address' : ''"
              class="mb-4 lh--force-white-background" />

            <v-btn color="primary" block size="large" type="submit" class="mb-4" :loading="showPreloader">
              Get Password Reset Link
            </v-btn>

            <v-alert v-if="errors.length > 0" type="error" variant="tonal" class="mb-4">
              <div v-for="error in errors" :key="error">{{ error }}</div>
            </v-alert>

            <div class="text-center mt-6">
              <router-link :to="{ name: 'login' }" class="text-decoration-none text-primary weight-500">
                Back to Login
              </router-link>
            </div>
          </v-form>
        </div>

        <div v-else class="text-center">
          <div class="mb-6">
            <v-icon size="64" color="primary">mdi-email-check</v-icon>
          </div>
          <p class="text-primary weight-500">Check your email, you will receive a link to reset your password.</p>
          <div class="mt-6">
            <router-link :to="{ name: 'login' }" class="text-decoration-none text-primary weight-500">
              Back to Login
            </router-link>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from "vue";
import authService from "@/services/authService";
import { tryParseApiErrors } from "@/utils";
import { FORM_VALIDATION_PATTERNS } from "@/utils/constants";

const errors = ref([]);
const showPreloader = ref(false);
const passwordRequestEmailSent = ref(false);
const userEmail = ref("");
const formButtonClicked = ref(false);

// Validators
const isEmailValid = computed(() => {
  const email = userEmail.value;
  return email !== "" && FORM_VALIDATION_PATTERNS.EMAIL_PATTERN.test(email);
});

const passwordSending = async () => {
  formButtonClicked.value = true;

  if (isEmailValid.value) {
    showPreloader.value = true;
    try {
      await authService.passwordResetRequest(userEmail.value);
      passwordRequestEmailSent.value = true;
      userEmail.value = "";
      errors.value = [];
    } catch (error) {
      errors.value = tryParseApiErrors(error);
    } finally {
      showPreloader.value = false;
    }
  }
};
</script>

<style lang="scss" scoped>
@import "../styles/main.scss";

.auth-page {
  display: flex;
  flex-direction: column;
  flex: 1;
  background: #ffffff;
}

.auth-page__logo {
  height: 36px;
  width: auto;
  margin-bottom: 32px;
}

.auth-page__content {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 0 16px 32px;
}

.auth-page__title {
  font-family: "DM Sans", sans-serif;
  font-size: 32px;
  font-weight: 700;
  color: #052e5e;
  margin-bottom: 32px;
}

.auth-page__card {
  width: 100%;
  max-width: 520px;
  background: #f4f6fa;
  border-radius: 16px;
  padding: 32px;
}
</style>
